/**
 * "Promo code" on the Edit / Add Location form.
 *
 * The value is the location's own code (its location CTA's promo), or USE_ORG_DEFAULT. Saving is
 * done by the parent form after the location itself saves (applyLocationPromo). Before saving,
 * the user sees what the change will not reach: printed QR codes, and upcoming events that already
 * carry their own code.
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useOrganizationPromoCodes } from "./PromoCodeSelect";
import {
  USE_ORG_DEFAULT,
  countUpcomingEventsKeepingTheirCode,
  type LocationPromoContext,
} from "../../services/locationPromo.service";
import type { UUID } from "../../types/common.types";

interface Props {
  organizationId: UUID;
  locationId?: UUID;
  orgDefaultCode: string;
  context: LocationPromoContext | undefined;
  contextLoading: boolean;
  value: string;
  onChange: (value: string) => void;
}

const LocationPromoField: React.FC<Props> = ({
  organizationId,
  locationId,
  orgDefaultCode,
  context,
  contextLoading,
  value,
  onChange,
}) => {
  const { data: codes = [], isLoading: codesLoading } =
    useOrganizationPromoCodes(organizationId);

  const currentCode = context?.currentCode ?? "";
  const changing =
    !contextLoading &&
    (value === USE_ORG_DEFAULT ? currentCode !== "" : value !== currentCode);
  const effectiveNew = value === USE_ORG_DEFAULT ? orgDefaultCode : value;
  const shownCurrent = currentCode || orgDefaultCode;

  const { data: keepingCount = 0 } = useQuery({
    queryKey: ["location-promo-events-keeping", locationId, effectiveNew],
    queryFn: () =>
      countUpcomingEventsKeepingTheirCode(locationId!, effectiveNew),
    enabled: !!locationId && changing,
  });

  const qrPrinted = !!context?.locationCta?.qr_code_url;
  const known = codes.some((c) => c.code === currentCode);
  const loading = contextLoading || codesLoading;

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Promo code
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
        aria-label="Promo code"
      >
        <option value={USE_ORG_DEFAULT}>
          {loading
            ? "Loading…"
            : `Use organization default (${orgDefaultCode || "none set"})`}
        </option>
        {codes.map((c) => (
          <option key={c.id} value={c.code}>
            {c.code}
          </option>
        ))}
        {currentCode && !known && !codesLoading && (
          <option value={currentCode}>
            {currentCode} — not on this organization&apos;s list
          </option>
        )}
      </select>
      <p className="mt-1 text-xs text-gray-500">
        The code this location&apos;s signup link and events use. Codes come
        from the organization&apos;s Promo Codes tab.
      </p>
      {changing && qrPrinted && (
        <p className="mt-1 text-xs text-amber-700">
          Printed QR codes for this location will still use{" "}
          {shownCurrent || "no code"} until they are reprinted. Saving updates
          the link and the QR image to download.
        </p>
      )}
      {changing && keepingCount > 0 && (
        <p className="mt-1 text-xs text-amber-700">
          Upcoming events at this location keep their current code:{" "}
          {keepingCount} {keepingCount === 1 ? "event" : "events"}. Change those
          on each event&apos;s Calls to Action tab.
        </p>
      )}
    </div>
  );
};

export default LocationPromoField;
